import { useCallback, useEffect, useState } from 'react';

const COLLAPSED_KEY = 'ezzo.sidebar.collapsed';
const GROUPS_KEY = 'ezzo.sidebar.groups';

function readBoolean(key: string, fallback: boolean): boolean {
  if (typeof window === 'undefined') return fallback;
  try {
    const value = window.localStorage.getItem(key);
    return value == null ? fallback : value === 'true';
  } catch {
    return fallback;
  }
}

function readGroups(): Record<string, boolean> {
  if (typeof window === 'undefined') return {};
  try {
    const value = window.localStorage.getItem(GROUPS_KEY);
    const parsed = value ? JSON.parse(value) : {};
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

function isMobileViewport(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches;
}

export type NavigationState = {
  collapsed: boolean;
  mobileOpen: boolean;
  moduleMenuOpen: boolean;
  isMobile: boolean;
  expandedGroups: Record<string, boolean>;
  /** Increments when UI should open nav search (navbar button, `/` hotkey). */
  searchFocusNonce: number;
  setCollapsed: (collapsed: boolean) => void;
  toggleCollapsed: () => void;
  setMobileOpen: (open: boolean) => void;
  setModuleMenuOpen: (open: boolean) => void;
  toggleGroup: (key: string) => void;
  ensureGroupOpen: (key: string) => void;
  setAllGroups: (keys: string[], open: boolean) => void;
  /** Expand sidebar/drawer and ask AdminSidebar to focus the menu search. */
  openNavForSearch: () => void;
};

/**
 * Shared shell navigation state.
 * Desktop collapse persists in localStorage.
 * Mobile drawer + module dropdown intentionally do NOT persist.
 */
export function useNavigationState(): NavigationState {
  const [isMobile, setIsMobile] = useState(isMobileViewport);
  const [collapsed, setCollapsedState] = useState(() =>
    readBoolean(COLLAPSED_KEY, typeof window !== 'undefined' && window.innerWidth < 1280),
  );
  const [mobileOpen, setMobileOpen] = useState(false);
  const [moduleMenuOpen, setModuleMenuOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(readGroups);
  const [searchFocusNonce, setSearchFocusNonce] = useState(0);

  const toggleCollapsed = useCallback(() => setCollapsedState((v) => !v), []);
  const openNavForSearch = useCallback(() => {
    // Always show the full sidebar (with the inline search field) before focusing.
    setCollapsedState(false);
    setMobileOpen(true);
    setSearchFocusNonce((n) => n + 1);
  }, []);
  const toggleGroup = useCallback(
    (key: string) =>
      setExpandedGroups((groups) => ({
        ...groups,
        [key]: !(groups[key] ?? true),
      })),
    [],
  );
  const ensureGroupOpen = useCallback(
    (key: string) =>
      setExpandedGroups((groups) =>
        groups[key] === false ? { ...groups, [key]: true } : groups,
      ),
    [],
  );
  const setAllGroups = useCallback((keys: string[], open: boolean) => {
    setExpandedGroups((groups) => {
      const next = { ...groups };
      keys.forEach((key) => {
        next[key] = open;
      });
      return next;
    });
  }, []);

  useEffect(() => {
    const media = window.matchMedia('(max-width: 1023px)');
    const onChange = () => setIsMobile(media.matches);
    onChange();
    media.addEventListener?.('change', onChange);
    return () => media.removeEventListener?.('change', onChange);
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(COLLAPSED_KEY, String(collapsed));
    } catch {
      /* private browsing */
    }
  }, [collapsed]);

  useEffect(() => {
    try {
      window.localStorage.setItem(GROUPS_KEY, JSON.stringify(expandedGroups));
    } catch {
      /* private browsing */
    }
  }, [expandedGroups]);

  // Leaving mobile closes overlays; body scroll locks while drawer is open.
  useEffect(() => {
    if (!isMobile) {
      setMobileOpen(false);
      setModuleMenuOpen(false);
      return;
    }
    if (mobileOpen) {
      const previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = previousOverflow;
      };
    }
    return undefined;
  }, [isMobile, mobileOpen]);

  return {
    collapsed,
    mobileOpen,
    moduleMenuOpen,
    isMobile,
    expandedGroups,
    searchFocusNonce,
    setCollapsed: setCollapsedState,
    toggleCollapsed,
    setMobileOpen,
    setModuleMenuOpen,
    toggleGroup,
    ensureGroupOpen,
    setAllGroups,
    openNavForSearch,
  };
}

/** Back-compat alias used by older imports. */
export const useSidebarState = useNavigationState;
export type SidebarState = NavigationState;
